import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { APIProvider, Map, Marker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { COMPANY_INFO, COVERAGE_ZONES, fadeInUp, staggerContainer } from "../data/constants";

// Tacámbaro, Michoacán coordinates - Centro del polígono de cobertura
const TACAMBARO_CENTER = { lat: 19.2290, lng: -101.4540 };

// Polígono de cobertura - Coordenadas reales de la zona de cobertura
const COVERAGE_POLYGON = [
  { lat: 19.2399604, lng: -101.4665435 },
  { lat: 19.2398389, lng: -101.4672945 },
  { lat: 19.2388259, lng: -101.4669941 },
  { lat: 19.2384815, lng: -101.4648913 },
  { lat: 19.2376103, lng: -101.4665006 },
  { lat: 19.2372254, lng: -101.4692901 },
  { lat: 19.2371038, lng: -101.4702342 },
  { lat: 19.2342472, lng: -101.4689253 },
  { lat: 19.2306409, lng: -101.4683889 },
  { lat: 19.2292835, lng: -101.4653419 },
  { lat: 19.228007, lng: -101.4656423 },
  { lat: 19.2221313, lng: -101.4654277 },
  { lat: 19.2159718, lng: -101.4619945 },
  { lat: 19.2151613, lng: -101.4593337 },
  { lat: 19.2177548, lng: -101.4578746 },
  { lat: 19.2210372, lng: -101.45869 },
  { lat: 19.2219693, lng: -101.4589475 },
  { lat: 19.2218882, lng: -101.4540122 },
  { lat: 19.2249274, lng: -101.4525102 },
  { lat: 19.2235091, lng: -101.44706 },
  { lat: 19.2238333, lng: -101.4446138 },
  { lat: 19.2252921, lng: -101.4448284 },
  { lat: 19.2270751, lng: -101.4437555 },
  { lat: 19.2265078, lng: -101.4394639 },
  { lat: 19.2284528, lng: -101.4381765 },
  { lat: 19.2293442, lng: -101.4385627 },
  { lat: 19.2280476, lng: -101.4401935 },
  { lat: 19.2276829, lng: -101.4433263 },
  { lat: 19.2283717, lng: -101.4462016 },
  { lat: 19.2324238, lng: -101.4457296 },
  { lat: 19.2350981, lng: -101.4482616 },
  { lat: 19.2370431, lng: -101.4503644 },
  { lat: 19.2385423, lng: -101.4519523 },
  { lat: 19.2406492, lng: -101.4500211 },
  { lat: 19.2399604, lng: -101.4526819 },
  { lat: 19.2421079, lng: -101.455128 },
  { lat: 19.2427562, lng: -101.457188 },
  { lat: 19.2419863, lng: -101.4583896 },
  { lat: 19.2389474, lng: -101.4609645 },
  { lat: 19.2382586, lng: -101.4631961 },
  { lat: 19.2399604, lng: -101.4665435 },
];

// Componente para dibujar el polígono
const CoveragePolygon = () => {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google) return;

    // Crear el polígono de cobertura
    const coverageArea = new window.google.maps.Polygon({
      paths: COVERAGE_POLYGON,
      strokeColor: "#ff6600",
      strokeOpacity: 0.8,
      strokeWeight: 3,
      fillColor: "#ff6600",
      fillOpacity: 0.2,
      map: map,
    });

    // Crear el borde exterior (área sin cobertura con efecto de máscara)
    // Esto es opcional - crea un efecto de "spotlight"

    return () => {
      coverageArea.setMap(null);
    };
  }, [map]);

  return null;
};

const CoverageMap = () => {
  const [selectedZone, setSelectedZone] = useState(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const handleMarkerClick = useCallback((zone) => {
    setSelectedZone(zone);
  }, []);

  if (!apiKey) {
    return (
      <div className="h-[500px] bg-gray-800 rounded-3xl flex items-center justify-center">
        <p className="text-gray-400">Mapa no disponible - API Key no configurada</p>
      </div>
    );
  }

  return (
    <APIProvider apiKey={apiKey}>
      <Map
        style={{ width: "100%", height: "500px", borderRadius: "1.5rem" }}
        defaultCenter={TACAMBARO_CENTER}
        defaultZoom={14}
        gestureHandling="cooperative"
        disableDefaultUI={false}
        zoomControl={true}
        mapTypeControl={true}
        streetViewControl={false}
        fullscreenControl={true}
        mapTypeId="hybrid"
      >
        {/* Polígono de cobertura */}
        <CoveragePolygon />

        {/* Marcadores de zonas */}
        {COVERAGE_ZONES.map((zone) => (
          <Marker
            key={zone.id}
            position={{ lat: zone.lat, lng: zone.lng }}
            onClick={() => handleMarkerClick(zone)}
            title={zone.name}
            icon={{
              url: zone.status === "active" 
                ? "https://maps.google.com/mapfiles/ms/icons/orange-dot.png"
                : "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
              scaledSize: { width: 40, height: 40 }
            }}
          />
        ))}

        {selectedZone && (
          <InfoWindow
            position={{ lat: selectedZone.lat, lng: selectedZone.lng }}
            onCloseClick={() => setSelectedZone(null)}
          >
            <div className="p-2 min-w-[150px]">
              <h3 className="font-bold text-gray-900 text-base">{selectedZone.name}</h3>
              <p className={`text-sm font-medium ${
                selectedZone.status === "active" ? "text-green-600" : "text-yellow-600"
              }`}>
                {selectedZone.status === "active" ? "✓ Cobertura disponible" : "⏳ Próximamente"}
              </p>
            </div>
          </InfoWindow>
        )}
      </Map>
    </APIProvider>
  );
};

export const CoverageSection = () => {
  return (
    <section id="cobertura" className="py-24 bg-gray-900" data-testid="coverage-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="text-orange-500 font-semibold uppercase tracking-wider"
          >
            Zona de Cobertura
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold text-white mt-4"
          >
            Tacámbaro, Michoacán
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-xl text-gray-400 mt-4 max-w-2xl mx-auto">
            Consulta si tu zona ya cuenta con nuestra cobertura de fibra óptica
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          {/* Google Maps */}
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <CoverageMap />
          </div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2">
              <div className="w-4 h-4 rounded-full bg-orange-500" />
              <span className="text-white text-sm">Cobertura activa</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500" />
              <span className="text-white text-sm">En expansión</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2">
              <div className="w-4 h-4 rounded border-2 border-orange-500 bg-orange-500/20" />
              <span className="text-white text-sm">Área de cobertura</span>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-8">
            <a
              href={COMPANY_INFO.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-orange-500/30 transition-all"
              data-testid="coverage-cta"
            >
              <Phone className="w-5 h-5" />
              Consultar mi cobertura
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
